import React, { useState, useEffect } from 'react';
import { ApiWorkflow } from '../../hooks/useWorkflowApi';
import { ProgressBar } from './ProgressBar';
import { KeyAreaTag } from './KeyAreaTag';

interface WorkflowCardProps {
  workflow: ApiWorkflow;
  onView: () => void;
  onEdit: () => void;
  onJourneyMap: () => void;
  onToggleStatus: () => void;
  onDelete: () => void;
  canEdit: boolean;
}

const STATUS_BADGE_COLORS: Record<string, string> = {
  draft: '#888',
  active: '#1a6b3c',
  completed: '#c9a84c',
};

function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia(query).matches : false
  );

  useEffect(() => {
    const mql = window.matchMedia(query);
    const handler = (e: MediaQueryListEvent) => setMatches(e.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, [query]);

  return matches;
}

export function WorkflowCard({ workflow, onView, onEdit, onJourneyMap, onToggleStatus, onDelete, canEdit }: WorkflowCardProps) {
  const isWide = useMediaQuery('(min-width: 768px)');

  const completedSteps = workflow.steps.filter(s => s.status === 'completed').length;
  const totalSteps = workflow.steps.length;

  const badgeColor = STATUS_BADGE_COLORS[workflow.status] || '#888';

  const handleDelete = () => {
    const confirmed = window.confirm(`Are you sure you want to delete "${workflow.name}"?`);
    if (confirmed) {
      onDelete();
    }
  };

  const cardStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: isWide ? 'row' : 'column',
    alignItems: isWide ? 'center' : 'stretch',
    gap: '12px',
    padding: '12px 16px',
    borderRadius: '8px',
    backgroundColor: '#fff',
    border: '1px solid #e0e0e0',
    marginBottom: '8px',
  };

  const metaStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flex: isWide ? '0 0 auto' : undefined,
    minWidth: isWide ? '200px' : undefined,
  };

  const progressStyle: React.CSSProperties = {
    flex: isWide ? '1 1 auto' : undefined,
    minWidth: isWide ? '150px' : undefined,
  };

  const tagsStyle: React.CSSProperties = {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '4px',
    flex: isWide ? '0 1 auto' : undefined,
  };

  const actionsStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flex: isWide ? '0 0 auto' : undefined,
    marginTop: isWide ? undefined : '4px',
  };

  return (
    <div style={cardStyle}>
      {/* Workflow icon, name, and status badge */}
      <div style={metaStyle}>
        <span style={styles.icon}>{workflow.icon}</span>
        <span style={styles.name}>{workflow.name}</span>
        <span
          style={{
            ...styles.badge,
            backgroundColor: badgeColor,
          }}
        >
          {workflow.status}
        </span>
      </div>

      {/* Progress bar */}
      <div style={progressStyle}>
        <ProgressBar completed={completedSteps} total={totalSteps} />
      </div>

      {/* Key area tags — omit section if no key areas */}
      {workflow.keyAreas.length > 0 && (
        <div style={tagsStyle}>
          {workflow.keyAreas.map(ka => (
            <KeyAreaTag key={ka.keyAreaId} icon={ka.keyArea.icon} name={ka.keyArea.name} />
          ))}
        </div>
      )}

      {/* Action buttons */}
      <div style={actionsStyle}>
        <button style={styles.btnView} onClick={onView} title="View details">
          👁️ View
        </button>
        {canEdit && (
          <button style={styles.btnEdit} onClick={onEdit} title="Edit workflow">
            ✏️ Edit
          </button>
        )}
        <button style={styles.btnJourney} onClick={onJourneyMap} title="Journey Map">
          🗺️
        </button>
        {canEdit && (
          <button style={styles.btnDelete} onClick={handleDelete} title="Delete workflow">
            🗑️
          </button>
        )}
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  icon: {
    fontSize: '20px',
    flexShrink: 0,
  },
  name: {
    fontWeight: 600,
    fontSize: '14px',
    color: '#1a1a1a',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  badge: {
    display: 'inline-block',
    padding: '2px 8px',
    borderRadius: '9999px',
    color: '#fff',
    fontSize: '11px',
    fontWeight: 600,
    textTransform: 'capitalize',
    whiteSpace: 'nowrap',
  },
  btnView: {
    padding: '4px 10px',
    fontSize: '12px',
    fontWeight: 500,
    borderRadius: '4px',
    border: '1px solid #1a6b3c',
    backgroundColor: '#fff',
    color: '#1a6b3c',
    cursor: 'pointer',
  },
  btnEdit: {
    padding: '4px 10px',
    fontSize: '12px',
    fontWeight: 500,
    borderRadius: '4px',
    border: '1px solid #f59e0b',
    backgroundColor: '#fff',
    color: '#f59e0b',
    cursor: 'pointer',
  },
  btnJourney: {
    padding: '4px 10px',
    fontSize: '12px',
    fontWeight: 500,
    borderRadius: '4px',
    border: '1px solid #7c3aed',
    backgroundColor: '#fff',
    color: '#7c3aed',
    cursor: 'pointer',
  },
  btnStatus: {
    padding: '4px 10px',
    fontSize: '12px',
    fontWeight: 500,
    borderRadius: '4px',
    border: '1px solid #555',
    backgroundColor: '#fff',
    color: '#555',
    cursor: 'pointer',
  },
  btnDelete: {
    padding: '4px 10px',
    fontSize: '12px',
    fontWeight: 500,
    borderRadius: '4px',
    border: '1px solid #c0392b',
    backgroundColor: '#fff',
    color: '#c0392b',
    cursor: 'pointer',
  },
};
